import React, { useCallback, useRef, useState } from "react";
import { UploadCloud } from "lucide-react";

import { useToast } from "../../context/ToastContext";
import { PickedFiles, pickedFromDrop, pickedFromList } from "../../utils/pickedFiles";

interface AddFilesDropZoneProps {
	onFiles: (files: File[]) => void;
	/** The larger first-pick version, used before any file is chosen. */
	prominent?: boolean;
}

const ACCEPT = ".png,.jpg,.jpeg,.pdf,.xls,.xlsx,.doc,.docx,.md,.markdown,.txt,.zip";

/** Drop files or a folder, or browse for files, to add them to the review. */
const AddFilesDropZone: React.FC<AddFilesDropZoneProps> = ({
	onFiles,
	prominent = false,
}) => {
	const { showToast } = useToast();
	const [isDragOver, setIsDragOver] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const accept = useCallback(
		({ files, problems }: PickedFiles) => {
			if (problems.length > 0) showToast(problems.join(" "), "info");
			if (files.length > 0) onFiles(files);
		},
		[onFiles, showToast]
	);

	const handleDrop = (event: React.DragEvent) => {
		event.preventDefault();
		setIsDragOver(false);
		void pickedFromDrop(event.dataTransfer).then(accept);
	};

	const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
		accept(pickedFromList(event.target.files));
		// Allow picking the same file again after removing it.
		event.target.value = "";
	};

	return (
		<div
			onDragOver={(event) => {
				event.preventDefault();
				setIsDragOver(true);
			}}
			onDragLeave={() => setIsDragOver(false)}
			onDrop={handleDrop}
			className={`flex items-center gap-3 rounded-lg border-[1.5px] border-dashed transition-colors ${
				prominent ? "flex-col justify-center px-6 py-10 text-center" : "px-4 py-3"
			} ${isDragOver ? "border-primary-500 bg-primary-50" : "border-primary-200 bg-primary-50/40"}`}
		>
			<span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary">
				<UploadCloud className="h-[18px] w-[18px]" aria-hidden="true" />
			</span>
			<span className="text-base text-text-strong">
				{prominent ? "Drop files here, or " : "Drop more files here, or "}
				<button
					type="button"
					onClick={() => inputRef.current?.click()}
					className="font-semibold text-primary underline underline-offset-2 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
				>
					browse
				</button>
			</span>
			<span className={`text-xs text-text-muted ${prominent ? "" : "ml-auto"}`}>
				PDF, Excel, Word, images
			</span>
			<input
				ref={inputRef}
				type="file"
				multiple
				accept={ACCEPT}
				onChange={handleInput}
				className="hidden"
				aria-label="Choose files to add"
			/>
		</div>
	);
};

export default AddFilesDropZone;
